// Website-owned intake. Retired Reach/TMS commerce adapters remain untouched.
import { current as currentReferral } from "./referralCapture";
import { applicationCvError } from "./applicationCv";
const ROOT = "/api/site/v1";
const memoryKeys = new Map(),
  flights = new Map(),
  applicationProofs = new Map();
const publicError = (code) =>
  ({
    invalid_input: "Check the required fields and try again.",
    rate_limited: "Please wait before trying again.",
    invalid_continuation:
      "This application session is unavailable or expired. Reopen the assessment in the browser where you applied.",
    assessment_already_submitted: "This assessment has already been submitted.",
    role_not_open: "This role is not open yet. Choose Safety or ELD.",
    legal_text_not_published: "Applications open soon. Please try again later.",
    site_backend_disabled:
      "Website submissions are awaiting activation. Please try again later.",
    site_configuration_required:
      "Website submissions are awaiting configuration. Please try again later.",
  })[code] || "The website could not complete this request. Please try again.";
export async function publicRequest(
  path,
  { method = "GET", body, key, signal } = {},
) {
  if (
    !/^\/(resources|intake)(\/|\?|$)/.test(path) ||
    path.includes("..") ||
    path.includes("://")
  )
    throw new Error("Invalid public API path");
  const abort = new AbortController(),
    cancel = () => abort.abort(),
    timer = setTimeout(cancel, 20000);
  signal?.addEventListener("abort", cancel, { once: true });
  if (signal?.aborted) cancel();
  try {
    const response = await fetch(ROOT + path, {
      method,
      signal: abort.signal,
      credentials: "same-origin",
      cache: "no-store",
      redirect: "error",
      referrerPolicy: "no-referrer",
      headers: body
        ? { "Content-Type": "application/json", "Idempotency-Key": key }
        : {},
      body: body ? JSON.stringify(body) : undefined,
    });
    const result = await response.json().catch(() => null);
    if (!response.ok || !result || !("data" in result)) {
      const error = new Error(publicError(result?.error?.code));
      error.status = response.status;
      error.code = result?.error?.code;
      throw error;
    }
    return result.data;
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", cancel);
  }
}
async function keyFor(kind, body) {
  const bytes = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(JSON.stringify(body)),
  );
  const digest = Array.from(new Uint8Array(bytes), (b) =>
    b.toString(16).padStart(2, "0"),
  ).join("");
  const name = "magellan-site-operation:" + kind;
  let previous = memoryKeys.get(name);
  try {
    previous = JSON.parse(sessionStorage.getItem(name)) || previous;
  } catch {}
  if (previous?.digest === digest && previous.expiresAt > Date.now())
    return previous.key;
  const entry = {
    digest,
    key: crypto.randomUUID(),
    expiresAt: Date.now() + 30 * 60000,
  };
  memoryKeys.set(name, entry);
  try {
    sessionStorage.setItem(name, JSON.stringify(entry));
  } catch {}
  return entry.key;
}
// Link-supplied text: control characters are dropped (the server refuses them as invalid_text),
// so a crafted utm_* value cannot stop a lead form or checkout.
// eslint-disable-next-line no-control-regex
const linkText = (value, max) => (value || "").replace(/[\u0000-\u001f\u007f]/g, "").slice(0, max);
// Shared by intake forms and website checkout (siteCommerceApi). The referral fields are added
// only when this browser holds a valid stored ?ref= code (D18), so other requests are unchanged.
export function attribution() {
  const q = new URLSearchParams(window.location.search);
  let referrer = "";
  try {
    const u = new URL(document.referrer);
    referrer = u.origin + u.pathname;
  } catch {}
  const result = {
    sourcePage: window.location.pathname.slice(0, 300),
    referrer: referrer.slice(0, 1000),
    utmSource: linkText(q.get("utm_source"), 100),
    utmMedium: linkText(q.get("utm_medium"), 100),
    utmCampaign: linkText(q.get("utm_campaign"), 200),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "",
  };
  const referral = currentReferral();
  return referral
    ? {
        ...result,
        referralCode: referral.code,
        referralCapturedAt: referral.capturedAt,
        referralLandingPath: referral.landingPath,
      }
    : result;
}
async function submit(kind, body) {
  try {
    const key = await keyFor(kind, body);
    if (!flights.has(key))
      flights.set(
        key,
        publicRequest("/intake/" + kind, { method: "POST", body, key }).finally(
          () => flights.delete(key),
        ),
      );
    const data = await flights.get(key);
    // Scoped assessment proof: never in URLs or the application draft. Firebase
    // Hosting only forwards __session; separate application cookies cannot work.
    if (kind === "application") {
      const proof = {
        applicationKey: key,
        continuationToken: data.continuationToken,
      };
      applicationProofs.set(data.id, proof);
      try {
        sessionStorage.setItem(
          "magellanAssessmentProof:" + data.id,
          JSON.stringify(proof),
        );
      } catch {}
    }
    const { continuationToken, ...receipt } = data;
    return { success: true, data: receipt };
  } catch (error) {
    return {
      success: false,
      error:
        error.name === "AbortError"
          ? "The connection timed out. Retry to check the same request."
          : publicError(error.code),
      retryable: !error.status || error.status >= 500,
    };
  }
}
export const extractCarrierFlowReference = (data) =>
  data?.receiptId || data?.id || "";
export const submitWebsiteContact = (form) =>
  submit("contact", { form, attribution: attribution() });
export const submitCompanyApplication = (form) =>
  submit("company", { form, attribution: attribution() });
export const submitJobApplication = (form) =>
  submit("application", { form, attribution: attribution() });
// The applicant's optional CV (L6), sent with the application's own proof.
export async function uploadApplicationCv(applicationId, file) {
  const proof = applicationProofs.get(applicationId);
  if (!proof || !file || applicationCvError(file)) return false;
  try {
    const r = await fetch(`/api/site/v1/intake/applications/${encodeURIComponent(applicationId)}/cv`, { method: "PUT", body: file,
      headers: { "Content-Type": "application/octet-stream", "Idempotency-Key": crypto.randomUUID(), "x-continuation-token": proof.continuationToken, "x-application-key": proof.applicationKey } });
    return r.ok;
  } catch { return false; }
}
export const submitPartnerApplication = (form) =>
  submit("partner", { form, attribution: attribution() });
export function submitJobApplicationAssessment(payload) {
  const answers = Object.fromEntries(
    Object.values(payload.assessment.sections)
      .flat()
      .map((q) => [q.id, q.answer]),
  );
  const id = payload.applicantReference;
  let proof = applicationProofs.get(id);
  try {
    proof =
      JSON.parse(sessionStorage.getItem("magellanAssessmentProof:" + id)) ||
      proof;
  } catch {}
  if (!proof)
    return Promise.resolve({
      success: false,
      error: publicError("invalid_continuation"),
    });
  return submit("assessment", { applicationId: id, ...proof, answers });
}
function resourceRequest(form, source, offer, type) {
  if (form.website)
    return Promise.resolve({
      success: false,
      error: "Unable to submit this request.",
    });
  const { website, ...fields } = form;
  return submit("resource", {
    form: {
      ...fields,
      [type + "Slug"]: source.slug || source.id || "",
      downloadOfferId:
        offer.offerId || offer.id || source.slug || source.id || "",
    },
    attribution: attribution(),
  });
}
export const submitServiceDownloadRequest = (
  form,
  { service = {}, offer = {} } = {},
) => resourceRequest(form, service, offer, "service");
export const submitResourceDownloadRequest = (
  form,
  { resource = {}, offer = {} } = {},
) => resourceRequest(form, resource, offer, "resource");

export function saveApplicationDraft(
  value,
  namespace = "magellanCareersAssessmentDraft",
) {
  const id = value.applicantReference;
  const draft = { applicantReference: id, createdAt: value.createdAt };
  try {
    sessionStorage.setItem(namespace + ":" + id, JSON.stringify(draft));
    sessionStorage.setItem(namespace, JSON.stringify(draft));
  } catch {}
}
export function readApplicationDraft(
  namespace = "magellanCareersAssessmentDraft",
) {
  const id = new URLSearchParams(window.location.search).get("application");
  if (id && !/^[A-Za-z0-9_-]{1,100}$/.test(id)) return null;
  try {
    return id
      ? JSON.parse(sessionStorage.getItem(namespace + ":" + id)) || {
          applicantReference: id,
        }
      : JSON.parse(sessionStorage.getItem(namespace));
  } catch {
    return id ? { applicantReference: id } : null;
  }
}
export function clearApplicationDraft(id) {
  applicationProofs.delete(id);
  try {
    sessionStorage.removeItem("magellanAssessmentProof:" + id);
  } catch {}
  try {
    sessionStorage.removeItem("magellanCareersAssessmentDraft:" + id);
    sessionStorage.removeItem("magellanAssessmentAnswers:" + id);
    if (
      JSON.parse(sessionStorage.getItem("magellanCareersAssessmentDraft"))
        ?.applicantReference === id
    )
      sessionStorage.removeItem("magellanCareersAssessmentDraft");
  } catch {}
}
export function loadAssessmentAnswers(id, initial) {
  try {
    const saved =
      JSON.parse(sessionStorage.getItem("magellanAssessmentAnswers:" + id)) ||
      {};
    return Object.fromEntries(
      Object.keys(initial).map((k) => [
        k,
        typeof saved[k] === "string" ? saved[k] : "",
      ]),
    );
  } catch {
    return initial;
  }
}
export function saveAssessmentAnswers(id, answers) {
  try {
    sessionStorage.setItem(
      "magellanAssessmentAnswers:" + id,
      JSON.stringify(answers),
    );
  } catch {}
}
